import {
    getCurrentLocaleKey,
    helpSourceIntlInit,
    ILocales,
    localeImageIntlInit,
    pick,
    textIntlInit
} from '~/utils';
import { useState } from 'react';
import dayjs from 'dayjs';
import DAY_JS_DE from 'dayjs/locale/de';
import DAY_JS_EN from 'dayjs/locale/en';
import DAY_JS_ES from 'dayjs/locale/es';
import DAY_JS_FR from 'dayjs/locale/fr';
import DAY_JS_ID from 'dayjs/locale/id';
import DAY_JS_JA from 'dayjs/locale/ja';
import DAY_JS_KM from 'dayjs/locale/km';
import DAY_JS_LO_LA from 'dayjs/locale/lo';
import DAY_JS_HI_IN from 'dayjs/locale/hi';
import DAY_JS_BN_BD from 'dayjs/locale/bn';
import DAY_JS_TA_IN from 'dayjs/locale/ta';
import DAY_JS_UZ_UZ from 'dayjs/locale/uz-latn';
import DAY_JS_MN_MN from 'dayjs/locale/mn';
import DAY_JS_IT_IT from 'dayjs/locale/it';
import DAY_JS_NL_NL from 'dayjs/locale/nl';
import DAY_JS_PL_PL from 'dayjs/locale/pl';
import DAY_JS_HU_HU from 'dayjs/locale/hu';
import DAY_JS_PT_BR from 'dayjs/locale/pt-br';
import DAY_JS_KO from 'dayjs/locale/ko';
import DAY_JS_PT from 'dayjs/locale/pt';
import DAY_JS_RU from 'dayjs/locale/ru';
import DAY_JS_TH from 'dayjs/locale/th';
import DAY_JS_VI from 'dayjs/locale/vi';
import DAY_JS_ZH_CN from 'dayjs/locale/zh-cn';
import DAY_JS_ZH_TW from 'dayjs/locale/zh-tw';
import { useComponentConstructor } from '@libs/shared';
import { Region } from '@fx-ui/platform-sdk';
import { LOCALE_KEY } from '@fx-ui/intl';

const dayjsLocale: Record<LOCALE_KEY, any> = {
    [LOCALE_KEY.ZH_CN]: DAY_JS_ZH_CN,
    [LOCALE_KEY.ZH_TW]: DAY_JS_ZH_TW,
    [LOCALE_KEY.EN_US]: DAY_JS_EN,
    [LOCALE_KEY.JA_JP]: DAY_JS_JA,
    [LOCALE_KEY.VI_VN]: DAY_JS_VI,
    [LOCALE_KEY.RU_RU]: DAY_JS_RU,
    [LOCALE_KEY.FR_FR]: DAY_JS_FR,
    [LOCALE_KEY.ES_ES]: DAY_JS_ES,
    [LOCALE_KEY.TH_TH]: DAY_JS_TH,
    [LOCALE_KEY.ID_ID]: DAY_JS_ID,
    [LOCALE_KEY.KO_KR]: DAY_JS_KO,
    [LOCALE_KEY.DE_DE]: DAY_JS_DE,
    [LOCALE_KEY.PT_PT]: DAY_JS_PT,
    [LOCALE_KEY.KM_KH]: DAY_JS_KM,
    [LOCALE_KEY.LO_LA]: DAY_JS_LO_LA,
    [LOCALE_KEY.HI_IN]: DAY_JS_HI_IN,
    [LOCALE_KEY.BN_BD]: DAY_JS_BN_BD,
    [LOCALE_KEY.TA_IN]: DAY_JS_TA_IN,
    [LOCALE_KEY.UZ_UZ]: DAY_JS_UZ_UZ,
    [LOCALE_KEY.MN_MN]: DAY_JS_MN_MN,
    [LOCALE_KEY.IT_IT]: DAY_JS_IT_IT,
    [LOCALE_KEY.NL_NL]: DAY_JS_NL_NL,
    [LOCALE_KEY.PL_PL]: DAY_JS_PL_PL,
    [LOCALE_KEY.HU_HU]: DAY_JS_HU_HU,
    [LOCALE_KEY.PT_BR]: DAY_JS_PT_BR
};

const useIntl = (extraLocales?: ILocales | ILocales[], region?: Region) => {
    const [initialized, setInitialized] = useState(false);
    const [currentLocale, setCurrentLocale] = useState(() => getCurrentLocaleKey());

    const initDayjs = (region?: Region) => {
        const currentLocale = getCurrentLocaleKey();
        dayjs.locale({
            ...dayjsLocale[currentLocale],
            // dayjs的weekStart和yearStart跟随租户地区，组件库dayjs会继承此处的设置
            ...pick(region, ['weekStart', 'yearStart'])
        });
    };

    const init = (region?: Region) => {
        const currentLocale = getCurrentLocaleKey();
        // dayJs 国际化
        initDayjs(region);

        // 帮助文档国际化
        helpSourceIntlInit(currentLocale);

        localeImageIntlInit(currentLocale);

        // 文本国际化
        textIntlInit(currentLocale, extraLocales).then(() => {
            setCurrentLocale(currentLocale);
            setInitialized(true);
        });
    };

    useComponentConstructor(() => {
        init(region);
    });

    return [initialized, init, currentLocale, initDayjs] as const;
};

export default useIntl;
